import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';

import { SkipLink } from '@/components/SkipLink';
import { AccountLinks, AccountMenu } from '@/components/public/AccountMenu';
import { CookieConsent } from '@/components/public/CookieConsent';
import { PageViewTracker } from '@/components/public/PageViewTracker';
import { Button } from '@/components/ui/button';
import {
  AWARDS,
  COPYRIGHT,
  FOOTER_BLURB,
  FOOTER_LINKS,
  HEADER_NAV,
  LOGO,
  NAP,
  awardAlt,
} from '@/lib/public-content';
import { cn } from '@/lib/utils';

/*
 * The public site's shell — ROADMAP.md §8.4.
 *
 * Header, footer, mobile nav, the real logo, the awards and the verified
 * contact details from business-rules.md §1. Every word is ported from
 * `reference/prototype-copy/public-site.md`; nothing here is written fresh.
 *
 * **This is the only surface with real-world value today** — the careers form
 * is receiving genuine applications — so it is also the one place a rebuild
 * quietly changing the business's voice would be noticed by real people.
 */

export function PublicLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-white font-sans text-ink-900">
      <SkipLink />
      {/*
        Both render nothing visible on a normal load. The counter reports the
        page and stops; the consent banner does not exist at all until a Google
        measurement ID is configured. Here rather than in App.tsx so the staff
        portals are never counted as website traffic.
      */}
      <PageViewTracker />
      <PublicHeader />
      <main id="main" tabIndex={-1} className="flex-1">
        <Outlet />
      </main>
      <PublicFooter />
      <CookieConsent />
    </div>
  );
}

function Wordmark() {
  return (
    <Link
      to="/"
      className="flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-700 focus-visible:ring-offset-2"
    >
      <img src={LOGO.src} alt={LOGO.alt} width={40} height={40} className="h-10 w-10" />
      <span className="leading-none">
        {/*
          The wordmark is `Honeycomb` over `CLEANING SERVICES`, as captured.
          **In `bark-700`, not the brand yellow** — design-system.md §3, and the
          exact mistake P3 records: the prototype renders its own name in
          #FFDD33 on white at 1.34:1, which is invisible.
        */}
        <span className="block text-[20px] font-bold tracking-tight text-bark-700">Honeycomb</span>
        <span className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-500">
          Cleaning Services
        </span>
      </span>
    </Link>
  );
}

function PublicHeader() {
  const location = useLocation();

  /*
   * **The drawer's open state is derived from the route, not synced to it.**
   *
   * It stores *which page* it was opened on, so navigating anywhere closes it
   * for free — including via the back button, which an `onClick` handler would
   * miss. The obvious version is a `useEffect` calling `setOpen(false)` on a
   * pathname change, and that is a cascading render for something the render
   * can already work out.
   *
   * Without any of this, tapping a link on a phone changes the page behind a
   * menu that is still covering it.
   */
  const [openAt, setOpenAt] = useState<string | null>(null);
  const open = openAt === location.pathname;

  const setOpen = (next: boolean) => {
    setOpenAt(next ? location.pathname : null);
  };

  // Escape closes it, which is the keyboard expectation for anything that
  // covers the page. `setOpenAt` rather than the `setOpen` wrapper, because a
  // state setter is stable across renders and the wrapper is not — depending on
  // the wrapper would re-bind the listener on every keystroke elsewhere.
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpenAt(null);
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /*
   * The header sits flat on the hero and gains a border and a shadow once the
   * page moves under it.
   *
   * A permanent border is a hairline across the top of a hero that has no
   * business being divided; no border at all makes the nav swim over
   * photographs further down. Reacting to scroll is what a header on a current
   * site does, and it is the cheapest single thing that stops one feeling
   * static.
   *
   * `passive: true` on the listener — this never calls `preventDefault`, and
   * without the hint the browser has to wait for the handler before it can
   * scroll, which is a stutter on a phone.
   */
  // Initial value read at first render, not in the effect — a page restored
  // mid-scroll (back button, a reload on a long page) should draw the border
  // immediately rather than flash without it.
  const [scrolled, setScrolled] = useState(
    () => typeof window !== 'undefined' && window.scrollY > 8,
  );

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <header
      className={cn(
        'sticky top-0 z-40 bg-white/85 backdrop-blur-md transition-[box-shadow,border-color,background-color] duration-300',
        scrolled || open ? 'border-b border-ink-300 shadow-card' : 'border-b border-transparent',
      )}
    >
      <div className="mx-auto flex max-w-content items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Wordmark />

        <nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
          {HEADER_NAV.map((item) => (
            <HeaderLink key={item.label} to={item.to} label={item.label} />
          ))}

          {/* Sign in, or the account menu. Replaces the old "My Schedule" link
              — see public-content.ts for why that one had to go. */}
          <AccountMenu />

          <Button asChild className="ml-2 shadow-card transition-shadow hover:shadow-pop">
            <Link to="/contact#quote">Get a Free Quote</Link>
          </Button>
        </nav>

        <button
          type="button"
          // 44×44 minimum — design-system.md §9.6, and the prototype fails this
          // at every measured point.
          className="inline-flex min-h-touch min-w-touch items-center justify-center rounded-md text-bark-700 hover:bg-honey-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-700 focus-visible:ring-offset-2 lg:hidden"
          aria-expanded={open}
          aria-controls="mobile-nav"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => {
            setOpen(!open);
          }}
        >
          {open ? <X className="size-6" aria-hidden /> : <Menu className="size-6" aria-hidden />}
        </button>
      </div>

      {open ? (
        <div
          id="mobile-nav"
          className="border-t border-ink-300 bg-white px-4 pb-4 pt-2 sm:px-6 lg:hidden"
        >
          <nav aria-label="Main" className="flex flex-col">
            {HEADER_NAV.map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-touch items-center rounded-md px-3 text-label',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-700',
                    isActive ? 'bg-honey-400 text-ink-900' : 'text-bark-700 hover:bg-honey-50',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}

            {/*
              Flattened rather than a dropdown. A menu inside a slide-over is two
              escape keys deep and easy to get lost in on a phone, and the
              drawer has the room to simply list them.
            */}
            <div className="mt-1 border-t border-ink-300 pt-1">
              <AccountLinks
                onNavigate={() => {
                  setOpen(false);
                }}
              />
            </div>

            <Button asChild full className="mt-3">
              <Link to="/contact#quote">Get a Free Quote</Link>
            </Button>
          </nav>
        </div>
      ) : null}
    </header>
  );
}

/**
 * A nav link.
 *
 * `end` on `/` so the home link is not active on every page — the exact-segment
 * rule from design-system.md §6, which is defect P4 in the prototype
 * (`/admin/calendar` lighting up on `/admin/calendar-sync`).
 */
function HeaderLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          'inline-flex min-h-touch items-center rounded-md px-3 text-label transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-700 focus-visible:ring-offset-2',
          isActive ? 'bg-honey-400 text-ink-900' : 'text-bark-700 hover:bg-honey-50',
        )
      }
    >
      {label}
    </NavLink>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-ink-300 bg-ink-50">
      <div className="mx-auto max-w-content px-4 py-12 sm:px-6">
        <div className="grid gap-10 md:grid-cols-3">
          <div>
            <Wordmark />
            <p className="mt-4 max-w-sm text-body text-ink-700">{FOOTER_BLURB}</p>

            <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
              Best of Iron County
            </p>
            <ul className="mt-3 flex flex-wrap items-center gap-3">
              {AWARDS.map((award) => (
                <li key={award.year}>
                  <img
                    src={award.src}
                    alt={awardAlt(award.year)}
                    width={64}
                    height={64}
                    className="h-16 w-16"
                    loading="lazy"
                  />
                </li>
              ))}
            </ul>
          </div>

          <nav aria-label="Quick links">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
              Quick Links
            </h2>
            <ul className="mt-3">
              {FOOTER_LINKS.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    className="inline-flex min-h-touch items-center text-body text-bark-700 hover:text-bark-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-700"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
              Contact Us
            </h2>
            <ul className="mt-3 space-y-1">
              <li>
                <a
                  href={NAP.phoneHref}
                  className="inline-flex min-h-touch items-center text-body text-bark-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-700"
                >
                  {NAP.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${NAP.email}`}
                  className="inline-flex min-h-touch items-center break-all text-body text-bark-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-700"
                >
                  {NAP.email}
                </a>
              </li>
              <li className="py-2 text-body text-ink-700">
                {NAP.city}, {NAP.stateName}
              </li>
              <li className="text-body text-ink-700">{NAP.hours}</li>
            </ul>

            {/*
              **No Facebook link.** business-rules.md §9 question 2 — the two
              sites link different pages and the documented default is to omit
              it rather than send customers to a page the business may not run.
              Instagram is unambiguous, so it stays.
            */}
            <a
              href={NAP.instagram}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-3 inline-flex min-h-touch items-center text-body text-bark-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-700"
            >
              Instagram
            </a>
          </div>
        </div>

        <p className="mt-10 border-t border-ink-300 pt-6 text-meta text-ink-500">{COPYRIGHT}</p>
      </div>
    </footer>
  );
}
